/**
 * Совместное редактирование в браузере (ADR-011): один WebSocket `/collab` на все открытые материалы,
 * по провайдеру Hocuspocus на документ, карту или доску. Права проверяет сервер (cookie сессии).
 */
import { HocuspocusProvider, HocuspocusProviderWebsocket } from '@hocuspocus/provider';
import { GRAPH_LISTS } from '@socrat/shared';
import * as Y from 'yjs';

/** Источник своих правок карты — для UndoManager (отмена только своих действий, не чужих). */
export const LOCAL = { local: true };

export type Peer = { id: string; name: string; color: string };

export type Session = {
  id: string;
  doc: Y.Doc;
  provider: HocuspocusProvider;
  synced: boolean;
  denied: boolean;
  undo: Y.UndoManager | null;
  peers: Peer[];
  closeTimer?: ReturnType<typeof setTimeout>;
};

const COLORS = ['#f7701f', '#3e63dd', '#30a46c', '#8e4ec6', '#d6409f', '#0d9488', '#e5484d', '#8a6200'];
export const colorOf = (id: string) =>
  COLORS[[...id].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7) % COLORS.length]!;

const wsUrl = () => `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/collab`;

export class CollabHub {
  private socket: HocuspocusProviderWebsocket | null = null;
  readonly sessions = new Map<string, Session>();
  private me: Peer | null = null;

  constructor(
    private readonly events: {
      /** Что-то изменилось (синхронизация, участники, отказ в доступе) — перерисовать. */
      change: (id: string) => void;
      /** Чужие правки карты или доски. */
      remote: (id: string) => void;
      /** Сообщение сервера (restored, error). */
      stateless: (id: string, msg: { type: string; [k: string]: unknown }) => void;
    },
  ) {}

  setUser(id: string, name: string) {
    this.me = { id, name, color: colorOf(id) };
    for (const s of this.sessions.values()) s.provider.setAwarenessField('peer', this.me);
  }

  get user() {
    return this.me;
  }

  /** Открыть (или переиспользовать) сессию материала. Закрытие — с задержкой: вернулись быстро — без переподключения. */
  open(id: string): Session {
    const existing = this.sessions.get(id);
    if (existing) {
      clearTimeout(existing.closeTimer);
      return existing;
    }
    this.socket ??= new HocuspocusProviderWebsocket({ url: wsUrl() });
    const doc = new Y.Doc();
    const s: Session = {
      id,
      doc,
      provider: null as unknown as HocuspocusProvider,
      synced: false,
      denied: false,
      undo: null,
      peers: [],
    };
    s.provider = new HocuspocusProvider({
      websocketProvider: this.socket,
      name: id,
      document: doc,
      token: 'cookie', // настоящая авторизация — cookie сессии в запросе WebSocket
      onSynced: () => {
        if (s.synced) return;
        s.synced = true;
        s.undo = new Y.UndoManager(GRAPH_LISTS.map((k) => doc.getMap(k)).concat([doc.getMap('style')]), {
          trackedOrigins: new Set([LOCAL]),
          captureTimeout: 400,
        });
        this.events.change(id);
      },
      onAuthenticationFailed: () => {
        s.denied = true;
        this.events.change(id);
      },
      onAwarenessChange: ({ states }) => {
        const seen = new Set<string>();
        s.peers = states
          .map((st) => st.peer as Peer | undefined) // поле user занимает BlockNote (курсоры: имя и цвет)
          .filter((u): u is Peer => !!u && !!u.id && u.id !== this.me?.id && !seen.has(u.id) && !!seen.add(u.id));
        this.events.change(id);
      },
      onStateless: ({ payload }) => {
        try {
          this.events.stateless(id, JSON.parse(payload));
        } catch {
          /* не JSON — не наше */
        }
      },
    });
    s.provider.attach();
    if (this.me) s.provider.setAwarenessField('peer', this.me);
    doc.on('update', (_u: Uint8Array, origin: unknown) => {
      // свои правки карты (LOCAL) и редактора документа (y-prosemirror) не перерисовываем — только чужие
      if (s.synced && origin !== LOCAL && origin instanceof HocuspocusProvider) this.events.remote(id);
    });
    this.sessions.set(id, s);
    return s;
  }

  /** Закрыть сессию через delayMs (несохранённого не бывает: Yjs отправляет правки сразу). */
  release(id: string, delayMs = 20_000) {
    const s = this.sessions.get(id);
    if (!s || s.closeTimer) return;
    s.closeTimer = setTimeout(() => this.drop(id), delayMs);
  }

  private drop(id: string) {
    const s = this.sessions.get(id);
    if (!s) return;
    s.undo?.destroy();
    s.provider.destroy();
    s.doc.destroy();
    this.sessions.delete(id);
  }

  /** Есть неотправленные правки (выход со страницы — предупредить). */
  get pending() {
    return [...this.sessions.values()].some((s) => s.provider.hasUnsyncedChanges);
  }

  destroy() {
    for (const id of [...this.sessions.keys()]) this.drop(id);
    this.socket?.destroy();
    this.socket = null;
  }
}
